/**
 * Reference from this https://phrase.com/blog/posts/step-step-guide-javascript-localization/
 */

// We can have as many locales here as we want, and use any locales we want. We have English and German as locales here as examples.

// data-i18n-key="myName"
// data-i18n-opt='{"username": "NAFFCOCloud"}' Note: It's used to pass dynamic value/variables
{
  /* <h1 class="text-accentMain" data-i18n-key="myName" data-i18n-opt='{"username": "NAFFCOCloud"}'>My name is {username}</h1> */
}

import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import defaultTranslations from "/WebInterface/new-ui/modules/localizations/language/default.en.js";

const isSupportedLanguageCode = (language) => {
  if (typeof language !== "string" || !/^[a-z]{2}$/.test(language)) {
    return false;
  }
  if (typeof utils !== "undefined" && utils.doWeSupportThisLanguage) {
    return utils.doWeSupportThisLanguage(language);
  }
  return true;
};

const resolveInitialLocale = () => {
  const stored = localStorage.getItem("selected-language");
  const hasStored =
    stored && stored !== "undefined" && stored !== "null";
  const customizations = window.customizations;
  const saveSelection =
    customizations?.login_page_save_languages_selection === "true";

  if (saveSelection && hasStored) {
    return isSupportedLanguageCode(stored) ? stored : "en";
  }

  const loginDefault = customizations?.login_page_default_language;
  if (loginDefault) {
    const code = String(loginDefault).trim();
    if (code && isSupportedLanguageCode(code)) return code;
  }

  if (hasStored) {
    return isSupportedLanguageCode(stored) ? stored : "en";
  }

  return "en";
};

const defaultLocale = resolveInitialLocale();

// The active locale
window.locale = "";
// Gets filled with active locale translations
window.translations = {};
let localeRequestGeneration = 0;
// When the page content is ready...
document.addEventListener("DOMContentLoaded", async () => {
  // Translate the page to the default locale

  document.documentElement.setAttribute("lang", defaultLocale);

  setLocale(defaultLocale);
  bindLocaleSwitcher(defaultLocale);
});

export const fetchTranslationsFor = async (newLocale) => {
  const language =
    !newLocale || newLocale === "undefined" || newLocale === "null"
      ? defaultLocale
      : newLocale;

  if (!utils.doWeSupportThisLanguage(language)) {
    return defaultTranslations;
  }

  try {
    const response = await utils.importModule(
      `/WebInterface/new-ui/localizations/${language}.js`
    );

    return response.default;
  } catch (error) {
    return defaultTranslations;
  }
};
// Load translations for the given locale and translate
// the page to this locale
const bindLocaleSwitcher = (initialValue) => {
  const switcher = _$("[data-i18n-switcher]");
  switcher.val(initialValue);

  switcher.on("change", (e) => {
    const selectedLanguage = e.target.value;
    // Set the locale to the selected option[value]
    switcher.val(selectedLanguage);
    setLocale(selectedLanguage);
    localStorage.setItem("selected-language", selectedLanguage);
    window.dispatchEvent(
      new CustomEvent("languageChanged", {
        detail: { language: selectedLanguage },
      })
    );
  });
};

// Replace the inner text of the given HTML element
// with the translation in the active locale,
// corresponding to the element's data-i18n-key
const translateElement = (element) => {
  const key = element.getAttribute("data-i18n-key");
  const options = JSON.parse(element.getAttribute("data-i18n-opt")) || {};
  const attrToUpdate = element.getAttribute("data-i18n-attr-to-update");
  const defValue =
    element.getAttribute("data-i18n-defaultValue") || element.innerText;

  if (element.getAttribute("data-i18n-allow-html")) {
    if (attrToUpdate) {
      element.setAttribute(
        attrToUpdate,
        utils.sanitizePure(translate(key, options, defValue))
      );
    } else {
      element.innerHTML = utils.sanitizePure(translate(key, options, defValue));
    }
  } else {
    if (attrToUpdate) {
      element.setAttribute(attrToUpdate, translate(key, options, defValue));
    } else {
      element.innerText = translate(key, options, defValue);
    }
  }
};

const translate = (key, interpolations = {}, defVal) => {
  const value = window.translations[key] || defaultTranslations[key] || defVal;
  return value ? interpolate(value, interpolations) : defVal || key;
};

// data-i18n-key attribute with the translation corresponding
// to its data-i18n-key
export const translatePage = () => {
  document.querySelectorAll("[data-i18n-key]").forEach(translateElement);
};

const setLocale = async (newLocale) => {
  if (
    !newLocale ||
    newLocale === "undefined" ||
    newLocale === "null"
  )
    return;

  const locale = utils.doWeSupportThisLanguage(newLocale)
    ? newLocale
    : "en";

  if (locale === window.locale) return;

  const thisRequestId = ++localeRequestGeneration;
  document.documentElement.setAttribute("lang", locale);

  const newTranslations = await fetchTranslationsFor(locale);

  if (thisRequestId !== localeRequestGeneration) {
    return;
  }

  const uploadIframe = document.querySelector("#fileUploadIframe");
  uploadIframe?.contentWindow?.postMessage(
    {
      key: "language",
      value: { newTranslations, newLocale: locale },
    },
    window.location.origin
  );

  const manageShareIframe = document.querySelector("#manageShareIframe");
  manageShareIframe?.contentWindow?.postMessage(
    {
      key: "language",
      value: locale,
    },
    window.location.origin
  );

  const revisionsIframe = document.querySelector("#revisionsIframe");
  revisionsIframe?.contentWindow?.postMessage(
    {
      key: "language",
      value: locale,
    },
    window.location.origin
  );

  window.locale = locale;
  window.translations = newTranslations;
  try {
    window.dispatchEvent(
      new CustomEvent("translationsApplied", {
        detail: { locale },
      })
    );
  } catch (e) {}
  translatePage();
};

// Convert a message like "Hello, {name}" to "Hello, World"
// given the interpolations object {name: "World"}
export const interpolate = (message, interpolations) => {
  return Object.keys(interpolations).reduce((interpolated, key) => {
    let value = interpolations[key];

    // Sanitize interpolation values to prevent XSS
    if (typeof value === "string") {
      // HTML-encode dangerous characters for display in dialogs/text
      value = value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }

    return interpolated.replace(new RegExp(`{\s*${key}\s*}`, "g"), value);
  }, message);
};

export const t = (key, interpolations = {}, defVal) => {
  const value = window.translations[key] || defaultTranslations[key] || defVal;
  return value ? interpolate(value, interpolations) : defVal || key;
};

export const setLanguage = (language = "en") => {
  const locale = utils.doWeSupportThisLanguage(language) ? language : "en";
  document.documentElement.setAttribute("lang", locale);

  setLocale(locale);
  _$("[data-i18n-switcher]").val(locale);
};

export const translateComponent = (component) => {
  if (!component) return;
  component.querySelectorAll("[data-i18n-key]").forEach(translateElement);
};
